// Google API (Drive/Slides) access, independent of how the user signed in to the app.

import Cookies from "js-cookie";
import { getAuthCookieDomain } from "./auth-client";

export const GOOGLE_CLIENT_ID = process.env.REACT_APP_TINUITI_GOOGLE_CLIENT_ID ?? "";

const DEFAULT_SCOPE = "profile email openid https://www.googleapis.com/auth/drive";
const COOKIE_LIFE_MS = 24 * 60 * 60 * 1000;
// The Google token behind the cookie lives about an hour; the expiration stamp marks it stale
// well before that.
const TOKEN_STALE_AFTER_MS = 30 * 60 * 1000;

const cookieOptions = (): Cookies.CookieAttributes => {
  const domain = getAuthCookieDomain();
  const expires = new Date(Date.now() + COOKIE_LIFE_MS);
  return domain ? { domain, expires } : { expires };
};

/**
 * Requests a Google API access token through the GIS token client (no prompt if the user already
 * granted consent for this scope) and stores it in the `google_api_access_token` cookie and its
 * expiration cookie. Never checks `login_method` or reads an ID token. `scope` defaults to
 * "profile email openid https://www.googleapis.com/auth/drive"; `prompt` to "". `hint`
 * pre-selects a Google account.
 */
export const ensureGoogleApiAccessToken = (
  scope?: string,
  prompt?: string,
  hint?: string
): Promise<string> =>
  new Promise((resolve, reject) => {
    const google = Reflect.get(window, "google");
    if (!google) {
      reject(new Error("Google identity services not available"));
      return;
    }
    const client = google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: scope ?? DEFAULT_SCOPE,
      auto_select: true,
      ...(hint ? { hint } : {}),
      callback: (response: { access_token?: string }) => {
        if (!response.access_token) {
          reject(new Error("No access token in Google response"));
          return;
        }
        Cookies.set("google_api_access_token", response.access_token, cookieOptions());
        Cookies.set(
          "google_api_access_token_expiration",
          String(Date.now() + TOKEN_STALE_AFTER_MS),
          cookieOptions()
        );
        resolve(response.access_token);
      },
      // A closed popup or a blocked request never reaches `callback`.
      error_callback: (err: { message?: string; type?: string }) => {
        reject(new Error(err?.message ?? err?.type ?? "Google sign-in was not completed"));
      },
    });
    client.requestAccessToken({ prompt: prompt ?? "" });
  });
